import React, { useEffect, useRef, useState } from 'react';
import { Typography } from 'antd';
import PropTypes from 'prop-types';

import EditorNavigation from './EditorNavigation';
import GlobalNotesEditor from './GlobalNotesEditor';
import SectionEditor from './SectionEditor';
import useStrategyEditor from './useStrategyEditor';

import './styles.scss';

/**
 * StrategyEditor - Main container for the strategy editor tab
 *
 * Architecture:
 * - EditorNavigation: Menu for selecting sections
 * - SectionEditor: Editor for strategy-specific breakdown sections
 * - GlobalNotesEditor: Editor for global notes
 * - useStrategyEditor: Central hook managing all state, debounce, and saves
 *
 * Key features:
 * - Flushes pending saves before switching sections (Option A behavior)
 * - Proper content sync when switching between sections
 * - Single debounce timer managed centrally
 */
export default function StrategyEditor({
    selectedPlanId,
    breakdownData,
    globalData,
    initialSection,
    isReadOnly = false
}) {
    const [selectedSection, setSelectedSection] = useState(initialSection || null);

    // Track content version to signal external content changes to editor
    const contentVersionRef = useRef(0);
    const [contentVersion, setContentVersion] = useState(0);

    const prevSectionRef = useRef(null);
    const prevPlanIdRef = useRef(null);

    // Central state management hook
    const { content, handleContentChange, flushPendingSave, isSaving } = useStrategyEditor({
        selectedPlanId,
        selectedSection,
        breakdownData,
        globalData
    });

    useEffect(() => {
        const sectionChanged = selectedSection !== prevSectionRef.current;
        const planChanged = selectedPlanId !== prevPlanIdRef.current;

        if (sectionChanged || planChanged) {
            contentVersionRef.current += 1;
            setContentVersion(contentVersionRef.current);
        }

        prevSectionRef.current = selectedSection;
        prevPlanIdRef.current = selectedPlanId;
    }, [selectedSection, selectedPlanId]);

    const handleApplyTemplate = (templateContent) => {
        handleContentChange(templateContent);
        contentVersionRef.current += 1;
        setContentVersion(contentVersionRef.current);
    };

    // Handle section selection - flush before switching
    const handleSelectSection = (newSection) => {
        if (newSection === selectedSection) return;

        // Flush any pending save before switching
        flushPendingSave();

        setSelectedSection(newSection);
    };

    // Render the appropriate editor based on selection
    const renderEditor = () => {
        // No plan selected and not on global section
        if (!selectedPlanId && selectedSection !== 'global') {
            if (selectedSection) {
                return (
                    <div className="strategy-editor-empty-state">
                        <Typography.Title level={4}>No Trading Plan Selected</Typography.Title>
                        <Typography.Text>
                            Please select a trading plan from the left sidebar to edit
                            strategy-specific notes. Use &quot;Global TradeBook&quot; for notes that
                            apply across all strategies.
                        </Typography.Text>
                    </div>
                );
            }

            return (
                <div className="strategy-editor-empty-state">
                    <Typography.Title level={4}>Strategy Subsections</Typography.Title>
                    <Typography.Text>
                        Select a section from the left menu to view and edit content
                    </Typography.Text>
                </div>
            );
        }

        // Global section selected
        if (selectedSection === 'global') {
            return (
                <GlobalNotesEditor
                    content={content}
                    contentVersion={contentVersion}
                    onChange={handleContentChange}
                    isSaving={isSaving}
                    isReadOnly={isReadOnly}
                />
            );
        }

        // Strategy-specific section selected
        if (selectedSection) {
            return (
                <SectionEditor
                    section={selectedSection}
                    content={content}
                    contentVersion={contentVersion}
                    onChange={handleContentChange}
                    onApplyTemplate={handleApplyTemplate}
                    isSaving={isSaving}
                    isReadOnly={isReadOnly}
                />
            );
        }

        // No section selected
        return (
            <div className="strategy-editor-empty-state">
                <Typography.Title level={4}>Strategy Subsections</Typography.Title>
                <Typography.Text>
                    Select a section from the left menu to view and edit content
                </Typography.Text>
            </div>
        );
    };

    return (
        <div className="strategy-editor-container">
            <div className="strategy-editor-sidebar">
                <EditorNavigation
                    selectedSection={selectedSection}
                    onSelectSection={handleSelectSection}
                />
            </div>
            <div className="strategy-editor-content">{renderEditor()}</div>
        </div>
    );
}

StrategyEditor.propTypes = {
    selectedPlanId: PropTypes.string,
    breakdownData: PropTypes.object,
    globalData: PropTypes.object,
    initialSection: PropTypes.string,
    isReadOnly: PropTypes.bool
};
